import { useCallback, useEffect, useSyncExternalStore } from "react";
import styles from "./ThemeToggle.module.css";

// ---------------------------------------------------------------------
// Theme is a top-level app-wide concern. Keeping the store outside the
// component means every ThemeToggle instance (there's usually one, but
// storybook and gallery hoist multiple) reads the same value.
//
// The store is subscribed with useSyncExternalStore so external
// mutations to <html data-theme> stay in sync — e.g. keyboard shortcut
// or a Chrome extension that toggles it.
// ---------------------------------------------------------------------

type Theme = "dark" | "light";
const STORAGE_KEY = "aster.theme";

function currentTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light"
    ? "light"
    : "dark";
}

function subscribe(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const observer = new MutationObserver(cb);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  return () => observer.disconnect();
}

function setThemeAttr(next: Theme) {
  if (next === "dark") {
    document.documentElement.removeAttribute("data-theme");
  } else {
    document.documentElement.setAttribute("data-theme", "light");
  }
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Storage may be blocked in embedded contexts. Non-fatal — theme
    // just won't persist across reloads. Still switches for the session.
  }
}

/** Read-only React hook. Prefer this in components that just need the value. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, currentTheme, () => "dark");
}

/**
 * ThemeToggle — fixed top-right chip. Sets `data-theme` on <html>,
 * persists to localStorage. Defaults to dark (see main.tsx).
 *
 * Positioned `fixed` so it survives page scrolling. Set `inline` to
 * render it inline (used inside the gallery to demonstrate).
 */
export function ThemeToggle({ inline = false }: { inline?: boolean }) {
  const theme = useTheme();

  const toggle = useCallback(() => {
    setThemeAttr(theme === "dark" ? "light" : "dark");
  }, [theme]);

  useEffect(() => {
    // On mount, hydrate from storage if <html> doesn't already carry
    // a theme attribute.
    if (document.documentElement.hasAttribute("data-theme")) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "light") {
        document.documentElement.setAttribute("data-theme", "light");
      }
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <button
      type="button"
      className={styles.toggle}
      onClick={toggle}
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      style={inline ? { position: "static" } : undefined}
    >
      <span
        aria-hidden
        className={`${styles.dot} ${theme === "dark" ? styles.dotDark : styles.dotLight}`}
      />
      {theme === "dark" ? "Dark" : "Light"}
    </button>
  );
}
